import type { AuditEventDto } from '@time-tracking/shared';
import { useCallback, useState } from 'react';

import '../../pages/admin/audit-log.css';

// Shared rendering for an audit-event feed: day-grouped, expandable rows.
// Used by the project detail "Activity" tab. The admin Audit Log pane has its
// own (older, identical) copy of this rendering; migrating it onto this shared
// component is a clean follow-up — see the PR for context.

// ── Tone helpers ──────────────────────────────────────────────────────────────

type Tone = 'ok' | 'warn' | 'danger' | 'create' | 'update' | 'lock' | 'system' | 'default';

function eventTone(eventType: string): Tone {
  if (eventType === 'TIMESHEET_APPROVED') return 'ok';
  if (
    eventType === 'TIMESHEET_RETURNED' ||
    eventType === 'TIMESHEET_UNAPPROVED' ||
    eventType === 'TIMESHEET_RECALLED' ||
    // Un-finalises a reported month, so it reads as a caution, not a routine edit.
    eventType === 'REPORTING_PERIOD_REOPENED' ||
    eventType === 'ASSIGNMENT_REMOVED' ||
    eventType === 'USER_ACTIVE_CHANGED' ||
    // AD sync's twin of the admin action above: same consequence for the person.
    eventType === 'USER_ACTIVE_SYNCED'
  )
    return 'warn';
  if (
    eventType === 'TIME_ENTRY_DELETED' ||
    eventType === 'LINE_CLASSIFICATION_REMOVED' ||
    eventType === 'TEAM_REMOVED_FROM_TASK' ||
    eventType.endsWith('_DELETED') ||
    eventType.endsWith('_REMOVED')
  )
    return 'danger';
  if (
    eventType === 'TIMESHEET_CREATED' ||
    eventType === 'TIMESHEET_LINE_CREATED' ||
    eventType === 'ASSIGNMENT_CREATED' ||
    eventType === 'LINE_CLASSIFICATION_CREATED' ||
    eventType === 'TEAM_ASSIGNED_TO_TASK' ||
    eventType.endsWith('_CREATED') ||
    eventType.endsWith('_ASSIGNED')
  )
    return 'create';
  if (
    eventType === 'PROJECT_LOCKED' ||
    eventType === 'TASK_LOCKED' ||
    // Closing a period locks its weeks; no `_LOCKED` suffix, so named here.
    eventType === 'REPORTING_PERIOD_CLOSED' ||
    eventType.endsWith('_LOCKED')
  )
    return 'lock';
  // System-provenance events: a sync or import run recorded as a whole, not a
  // single action a person took in the UI, so they share the system tone.
  // CATALOG_IMPORT_RUN carries the operator as actor, but is still a run summary.
  if (
    eventType === 'AD_SYNC_COMPLETED' ||
    eventType === 'TEAM_STRUCTURE_IMPORTED' ||
    eventType === 'CATALOG_IMPORT_RUN'
  )
    return 'system';
  if (
    eventType.endsWith('_UPDATED') ||
    eventType === 'USER_ROLE_CHANGED' ||
    // Matches no suffix rule, so it needs an explicit entry to be grouped with
    // the other role-change events. Note this does not change how it renders:
    // tone-update and tone-default are identical in audit-log.css. It sets
    // data-tone for semantics, and puts the event where a reader expects it if
    // the two tones ever diverge.
    eventType === 'USER_ROLE_SYNCED' ||
    eventType === 'TEAM_MEMBER_ROLE_CHANGED' ||
    eventType === 'TASK_ARCHIVED' ||
    // Reversible state changes on a project, so they follow TASK_ARCHIVED's
    // tone rather than the danger tone. Neither name ends in _REMOVED or
    // _DELETED, so the suffix rules above correctly do not claim them — but
    // that also means they fall through to 'default' without these entries.
    eventType === 'PROJECT_ARCHIVED' ||
    eventType === 'PROJECT_RESTORED' ||
    eventType === 'TIMESHEET_RESUBMITTED'
  )
    return 'update';
  return 'default';
}

function formatEventLabel(eventType: string): string {
  return eventType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Day grouping helpers ───────────────────────────────────────────────────────

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function ChevronIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ── Event row ─────────────────────────────────────────────────────────────────

interface EventRowProps {
  event: AuditEventDto;
  expanded: boolean;
  onToggle: () => void;
}

function EventRow({ event, expanded, onToggle }: EventRowProps) {
  const tone = eventTone(event.eventType);
  const hasDiff = event.beforeJson !== null || event.afterJson !== null;
  const hasDetail = event.detail !== null;
  const hasReason = event.reason !== null;
  const hasExpand = hasDiff || hasDetail || hasReason;

  // Concise accessible name so screen readers don't read the full concatenation
  // of every visible cell when navigating the list row by row.
  const rowLabel =
    `${formatEventLabel(event.eventType)}` +
    `${event.objectName ? ` — ${event.objectName}` : ''}` +
    ` by ${event.actor.displayName}, ${relativeTime(event.createdAt)}`;

  const cells = (
    <>
      {/* Caret column is always present (keeps the flex layout aligned across rows);
          the chevron only renders when there's something to expand. */}
      <span className="aud-caret" aria-hidden="true">
        {hasExpand && <ChevronIcon />}
      </span>

      <span className="aud-time" title={event.createdAt}>
        {relativeTime(event.createdAt)}
      </span>

      <span className="aud-actor">
        <span className="aud-avatar" aria-hidden="true">
          {event.actor.initials}
        </span>
        <span className="aud-actor-name">{event.actor.displayName}</span>
      </span>

      <span className={`ev-tag tone-${tone}`} data-testid="audit-event-tag" data-tone={tone}>
        {formatEventLabel(event.eventType)}
      </span>

      <span className="aud-obj">
        <span className="obj-chip" data-testid="audit-object-chip">
          {event.objectType}
        </span>
        {event.objectName && (
          <span className="aud-obj-name" title={event.objectName}>
            {event.objectName}
          </span>
        )}
      </span>
    </>
  );

  return (
    <div className={`audit-row${expanded ? ' open' : ''}${hasExpand ? '' : ' static'}`}>
      {hasExpand ? (
        <button
          type="button"
          className="audit-main"
          aria-expanded={expanded}
          aria-label={rowLabel}
          onClick={onToggle}
          data-testid="audit-row"
        >
          {cells}
        </button>
      ) : (
        // No before/after, detail, or reason → nothing to disclose. Render a
        // non-interactive row instead of a button so assistive tech isn't told it
        // can expand content that never appears (no toggle, no aria-expanded).
        <div className="audit-main" data-testid="audit-row">
          {cells}
        </div>
      )}

      {expanded && hasExpand && (
        <div className="audit-detail">
          {hasDiff && (
            <div className="aud-diff">
              <div className="aud-diff-col before">
                <div className="lbl">Before</div>
                <div className="val">
                  {event.beforeJson !== null ? (
                    JSON.stringify(event.beforeJson, null, 2)
                  ) : (
                    <span className="none">—</span>
                  )}
                </div>
              </div>
              <span className="aud-arrow">→</span>
              <div className="aud-diff-col after">
                <div className="lbl">After</div>
                <div className="val">
                  {event.afterJson !== null ? (
                    JSON.stringify(event.afterJson, null, 2)
                  ) : (
                    <span className="none">—</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {hasDetail && (
            <div className="aud-line">
              <span className="k">Detail</span>
              {event.detail}
            </div>
          )}

          {hasReason && (
            <div className="aud-line">
              <span className="k">Reason</span>
              {event.reason}
            </div>
          )}

          <div className="aud-meta">
            <span className="aud-meta-item">
              {new Date(event.createdAt).toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'long',
              })}
            </span>
            {event.actorRole && <span className="aud-meta-item">{event.actorRole}</span>}
            {event.correlationId && (
              <span className="aud-meta-item cid">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                {event.correlationId}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Day-grouped list ────────────────────────────────────────────────────────────

function groupByDay(
  events: AuditEventDto[],
): { key: string; label: string; events: AuditEventDto[] }[] {
  const grouped: { key: string; label: string; events: AuditEventDto[] }[] = [];
  let currentKey = '';
  let currentGroup: AuditEventDto[] = [];
  const flush = () => {
    if (currentGroup.length > 0) {
      const first = currentGroup[0];
      grouped.push({
        key: currentKey,
        label: first ? dayLabel(first.createdAt) : '',
        events: currentGroup,
      });
    }
  };
  for (const event of events) {
    const key = dayKey(event.createdAt);
    if (key !== currentKey) {
      flush();
      currentKey = key;
      currentGroup = [event];
    } else {
      currentGroup.push(event);
    }
  }
  flush();
  return grouped;
}

/**
 * Renders a list of audit events grouped by calendar day, with expandable rows.
 * Manages its own expand state, so callers just pass the events.
 */
export function AuditEventList({ events }: { events: AuditEventDto[] }) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((eventId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }, []);

  const grouped = groupByDay(events);

  return (
    <div className="audit-log">
      {grouped.map((group) => (
        <div key={group.key}>
          <h3 className="audit-day">{group.label}</h3>
          {group.events.map((event) => (
            <EventRow
              key={event.eventId}
              event={event}
              expanded={expandedIds.has(event.eventId)}
              onToggle={() => toggleExpand(event.eventId)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
